import React, {useState, useContext, useEffect} from 'react';
import './PgePlanningItem.scss';
import {I18nContext} from '../../contexts/i18nContext';
import {ProjectsContext} from '../../contexts/projectsContext';
import {ToastContext} from '../../contexts/toastContext';
import {DateInput, SelectInput, TextInput, Plus, Button, CheckboxInput, SectionBox} from '../_common';
import {formatValueAsCurrency, getNestedPropertyValue, setNestedPropertyValue} from '../../helpers/helpers';
import _ from 'lodash';

const PgePlanningItem = (
  { project, refreshProjects, connectionPointOptions, cableDiameterOptions, oversightUserOptions }
) => {
  const { t } = useContext(I18nContext);
  const { displayToast } = useContext(ToastContext);
  const { getSinglePgeProject, updatePgeProject } = useContext(ProjectsContext);
  const [projectToPlan, setProjectToPlan] = useState(project);
  const [formValues, setFormValues] = useState({
    investmentAddress: project?.investmentAddress ?? '',
    cost: project?.cost ?? '',
    comments: project?.comments ?? '',
    implementation: {
      staking: {
        stakingSent: project?.implementation?.staking?.stakingSent ?? false,
        stakingSentDate: project?.implementation?.staking?.stakingSentDate ?? '',
        surveyor: project?.implementation?.staking?.surveyor ?? '',
      },
      materials: {
        cables: project
          ? [...project?.implementation?.materials?.cables]
          : [{diameterSelectionId: '', pathLength: '', cableLength: ''}],
        connectionTypes: project
          ? [...project?.implementation?.materials?.connectionTypes]
          : [''],
        lengthOfTheSqueeze: project
          ? [...project?.implementation?.materials?.lengthOfTheSqueeze]
          : [''],
      },
      construction: {
        constructionDate: project?.implementation.construction.constructionDate ?? '',
        crewMechanicIds: project
          ? [...project?.implementation?.construction?.crewMechanicIds.filter(x => !!x)]
          : [],
        leavesUserIds: project
          ? [...project?.implementation?.construction?.leavesUserIds.filter(x => !!x)]
          : [],
      },
    }
  });

  useEffect(() => {
    if (!!project) {
      setProjectToPlan(project);
    }
  }, [project]);

  useEffect(() => {
    setFormValues(projectToPlan)
  }, [projectToPlan]);

  const getProject = async () => {
    const newProject = await getSinglePgeProject(project.id)
    setProjectToPlan(newProject);
    return newProject;
  };

  const changeValue = async (name, value, multiple, blur) => {
    const newValues = {...formValues};

    if (multiple) {
      if (!getNestedPropertyValue(newValues, name).includes(value.id ? value.id : value)) {
        const newValue = [...getNestedPropertyValue(newValues, name), value.id ? value.id : value];
        const updatedValues = setNestedPropertyValue(newValues, name, newValue);
        setFormValues(updatedValues);
      } else {
        const newValue = [...getNestedPropertyValue(newValues, name)];
        const index = newValue.indexOf(value.id ? value.id : value);
        newValue.splice(index, 1);
        const updatedValues = setNestedPropertyValue(newValues, name, newValue);
        setFormValues(updatedValues);
      }
    } else {
      if (name.includes(".")) {
        const updatedValues = setNestedPropertyValue(newValues, name, value);
        setFormValues(updatedValues);
      } else {
        newValues[name] = value.id ? value.id : value;
        setFormValues(newValues);
      }
    }

    if (blur) {
      await saveOnBlur(name, value.id ? value.id : value, multiple);
    }
  };

  const saveOnBlur = async (name, value, multiple) => {
    if (!!project) {
      const nameArray = name.split('.');
      let newValue = nameArray.length ? {[nameArray[0]]: formValues[nameArray[0]]} : {};

      if (multiple) {
        if (!getNestedPropertyValue(newValue, name).includes(value.id ? value.id : value)) {
          const updatedValue = [...getNestedPropertyValue(newValue, name), value.id ? value.id : value];
          newValue = setNestedPropertyValue(newValue, name, updatedValue);
        } else {
          const updatedValue = [...getNestedPropertyValue(newValue, name)];
          const index = updatedValue.indexOf(value.id ? value.id : value);
          updatedValue.splice(index, 1);
          newValue = setNestedPropertyValue(newValue, name, updatedValue);
        }
      } else {
        if (name.includes(".")) {
          newValue = setNestedPropertyValue(newValue, name, value);
        } else {
          newValue[name] = value;
        }
      }
      const result = await updatePgeProject(newValue, projectToPlan?.id, projectToPlan?.version, true);

      if (result.status && result.status === 412) {
        const newProjectObj = await getSinglePgeProject(projectToPlan.id);

        const currentValue = getNestedPropertyValue(projectToPlan, name);
        const updatedValue = getNestedPropertyValue(newProjectObj, name);

        if (_.isEqual(currentValue, updatedValue)) {
          let updatedNewValue = nameArray.length ? {[nameArray[0]]: newProjectObj[nameArray[0]]} : {};
          if (name.includes(".")) {
            updatedNewValue = setNestedPropertyValue(updatedNewValue, name, value);
          } else {
            updatedNewValue[name] = value;
          }
          await updatePgeProject(updatedNewValue, projectToPlan?.id, newProjectObj?.version, true);
          await getProject();
          displayToast(
            'info',
            t("Project data has been updated by other user. The form data has been updated.", "asIs"),
            7000
          );
        } else {
          await getProject();
          await changeValue(name, updatedValue);
          displayToast(
            'error',
            t(
              "The value which you are trying to edit has been updated by other user. The form data has been updated. Please try again if required.",
              "asIs"
            ),
            0,
            true
          );
        }
      } else {
        await getProject();
      }
    }
  };

  const markPlanningCompleted = async () => {
    const projectObj = await getSinglePgeProject(project.id);
    await updatePgeProject({toBePlanned: false}, project.id, projectObj?.version, true);
    refreshProjects();
  };

  return (
    <div className='PgePlanningItem'>
      <div className='planningItemCell'>
        <div className='mobileLabel'>
          {t('construction date')}
        </div>
        <DateInput
          name='implementation.construction.constructionDate'
          value={formValues.implementation.construction.constructionDate}
          onChange={changeValue}
        />
      </div>
      <div className='planningItemCell'>
        <div className='mobileLabel'>
          {t('investment address')}
        </div>
        <TextInput
          name='investmentAddress'
          onChange={changeValue}
          value={formValues.investmentAddress}
        />
      </div>
      <div className='planningItemCell'>
        <div className='mobileLabel'>
          {t('staking')}
        </div>
        <div className='groupBox'>
          <CheckboxInput
            label={t('staking sent')}
            name='implementation.staking.stakingSent'
            value={formValues.implementation.staking.stakingSent}
            onChange={changeValue}
          />
          <DateInput
            label={t('staking sent date')}
            name='implementation.staking.stakingSentDate'
            value={formValues.implementation.staking.stakingSentDate}
            onChange={changeValue}
          />
          <TextInput
            label={t('surveyor')}
            name='implementation.staking.surveyor'
            onChange={changeValue}
            value={formValues.implementation.staking.surveyor}
          />
        </div>
      </div>
      <div className='planningItemCell--large'>
        <div className='mobileLabel'>
          {t('materials')}
        </div>
        <Plus
          label={t('cables')}
          name='implementation.materials.cables'
          value={formValues.implementation.materials.cables}
          itemObject={{diameterSelectionId: '', pathLength: '', cableLength: ''}}
          onChange={changeValue}
          itemInputs={[
            {
              type: 'select',
              name: 'diameterSelectionId',
              label: t('diameter'),
              options: cableDiameterOptions,
              required: false
            },
            {type: 'text', name: 'pathLength', label: t('path length'), required: false},
            {type: 'text', name: 'cableLength', label: t('cable length'), required: false}
          ]}
          mini
        />
        <Plus
          label={t('connection types')}
          name='implementation.materials.connectionTypes'
          value={formValues.implementation.materials.connectionTypes}
          itemObject={''}
          onChange={changeValue}
          itemInputs={[
            {type: 'text', name: '', label: t('connection type'), required: false}
          ]}
          mini
        />
        <Plus
          label={t('lengths of squeeze')}
          name='implementation.materials.lengthOfTheSqueeze'
          value={formValues.implementation.materials.lengthOfTheSqueeze}
          itemObject={''}
          onChange={changeValue}
          itemInputs={[
            {type: 'text', name: '', label: t('length of squeeze'), required: false}
          ]}
          mini
        />
      </div>
      <div className='planningItemCell'>
        <div className='mobileLabel'>
          {t('construction crew')}
        </div>
        <SelectInput
          name='implementation.construction.crewMechanicIds'
          options={oversightUserOptions}
          onSelect={changeValue}
          value={formValues.implementation.construction.crewMechanicIds}
          placeholder={t('select')}
          multiple
        />
      </div>
      <div className='planningItemCell'>
        <div className='mobileLabel'>
          {t('leaves')}
        </div>
        <SelectInput
          name='implementation.construction.leavesUserIds'
          options={oversightUserOptions}
          onSelect={changeValue}
          value={formValues.implementation.construction.leavesUserIds}
          placeholder={t('select')}
          multiple
        />
      </div>
      <div className='planningItemCell'>
        <div className='mobileLabel'>
          {t('remarks') + " - " + t('construction work')}
        </div>
        <TextInput
          name='implementation.construction.comment'
          onChange={changeValue}
          value={formValues.implementation.construction.comment}
          area
        />
      </div>
      <div className='planningItemCell'>
        <div className='mobileLabel'>
          {t('remarks') + " - " + t('rights to the location')}
        </div>
        <TextInput
          name='implementation.parcelPartAcquisitionsComments'
          onChange={changeValue}
          value={formValues.implementation.parcelPartAcquisitionsComments}
          area
        />
      </div>
      <div className='planningItemCell'>
        <div className='mobileLabel'>
          {t('order cost')}
        </div>
        <TextInput
          name='cost'
          onChange={changeValue}
          value={formatValueAsCurrency(formValues.cost)}
          disabled
        />
      </div>
      <div className='planningItemActionCell'>
        <Button label={t('planning completed')} fullWidth={true} action={markPlanningCompleted}/>
      </div>
    </div>
  );
};

export default PgePlanningItem;
